// components/session/SessionManagement/styles.js
import styled, { css, keyframes } from 'styled-components';

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

export const SessionsContainer = styled.div`
    padding: ${props => props.theme.spacing.xl};
    background: ${props => props.theme.colors.background.alt};
    min-height: calc(100vh - 72px);
`;

export const SessionsHeader = styled.div`
    margin-bottom: ${props => props.theme.spacing.xl};

    h2 {
        font-size: ${props => props.theme.typography.sizes['2xl']};
        margin-bottom: ${props => props.theme.spacing.lg};
        color: ${props => props.theme.colors.text.primary};
    }
`;

export const TabList = styled.div`
    display: flex;
    gap: ${props => props.theme.spacing.md};
    border-bottom: 1px solid ${props => props.theme.colors.border};
    margin-bottom: ${props => props.theme.spacing.xl};
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        gap: ${props => props.theme.spacing.sm};
    }
`;

export const TabButton = styled.button`
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    color: ${props => props.active ? props.theme.colors.primary.main : props.theme.colors.text.secondary};
    border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary.main : 'transparent'};
    font-weight: ${props => props.active ? props.theme.typography.weights.semibold : props.theme.typography.weights.regular};
    transition: all ${props => props.theme.transitions.fast};
    white-space: nowrap;

    &:hover {
        color: ${props => props.theme.colors.primary.main};
    }

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        padding: ${props => `${props.theme.spacing.sm} ${props.theme.spacing.md}`};
        font-size: ${props => props.theme.typography.sizes.sm};
    }
`;

export const SessionsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: ${props => props.theme.spacing.lg};

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

export const SessionCard = styled.div`
    background: white;
    border-radius: ${props => props.theme.borderRadius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    overflow: hidden;
    transition: transform ${props => props.theme.transitions.normal}, 
                box-shadow ${props => props.theme.transitions.normal};

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${props => props.theme.shadows.md};
    }
`;

export const SessionInfo = styled.div`
    padding: ${props => props.theme.spacing.xl};

    .primary-info {
        margin-bottom: ${props => props.theme.spacing.lg};
    }

    .date-time {
        display: flex;
        align-items: center;
        gap: ${props => props.theme.spacing.sm};
        margin-bottom: ${props => props.theme.spacing.sm};

        .icon {
            color: ${props => props.theme.colors.primary.main};
            display: flex;
            align-items: center;
        }

        .text {
            color: ${props => props.theme.colors.text.primary};
            font-size: ${props => props.theme.typography.sizes.md};
        }
    }

    .notes {
        padding-top: ${props => props.theme.spacing.md};
        border-top: 1px solid ${props => props.theme.colors.border};
        
        p {
            color: ${props => props.theme.colors.text.secondary};
            font-size: ${props => props.theme.typography.sizes.sm};
            line-height: 1.5;
        }
    }
`;

export const SessionActions = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing.md};
    padding: ${props => props.theme.spacing.lg};
    background: ${props => props.theme.colors.background.alt};
    border-top: 1px solid ${props => props.theme.colors.border};

    button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${props => props.theme.spacing.sm};
        
        svg {
            width: 16px;
            height: 16px;
        }
    }

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

export const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    border-radius: ${props => props.theme.borderRadius.full};
    font-size: ${props => props.theme.typography.sizes.sm};
    font-weight: ${props => props.theme.typography.weights.medium};
    text-transform: capitalize;
    
    ${props => {
        switch (props.status) {
            case 'success':
                return css`
                    background: ${props.theme.colors.success.main}15;
                    color: ${props.theme.colors.success.main};
                `;
            case 'warning':
                return css`
                    background: ${props.theme.colors.warning.main}15;
                    color: ${props.theme.colors.warning.main};
                `;
            case 'error':
                return css`
                    background: ${props.theme.colors.error.main}15;
                    color: ${props.theme.colors.error.main};
                `;
            case 'info':
                return css`
                    background: ${props.theme.colors.primary.main}15;
                    color: ${props.theme.colors.primary.main};
                `;
            default:
                return css`
                    background: ${props.theme.colors.text.disabled}15;
                    color: ${props.theme.colors.text.disabled};
                `;
        }
    }}
`;

export const NoSessions = styled.div`
    text-align: center;
    padding: ${props => props.theme.spacing['3xl']};
    background: white;
    border-radius: ${props => props.theme.borderRadius.lg};
    grid-column: 1 / -1;
    color: ${props => props.theme.colors.text.secondary};

    svg {
        margin-bottom: ${props => props.theme.spacing.lg};
        opacity: 0.5;
    }

    h3 {
        font-size: ${props => props.theme.typography.sizes.xl};
        color: ${props => props.theme.colors.text.primary};
        margin-bottom: ${props => props.theme.spacing.sm};
    }
`;

export const LoadingSpinner = styled.div`
    position: relative;
    width: 40px;
    height: 40px;
    margin: ${props => props.theme.spacing['3xl']} auto;

    &:before {
        content: '';
        box-sizing: border-box;
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40px;
        height: 40px;
        margin-top: -20px;
        margin-left: -20px;
        border-radius: 50%;
        border: 3px solid ${props => props.theme.colors.text.disabled};
        border-top-color: ${props => props.theme.colors.primary.main};
        animation: ${spin} 0.8s linear infinite;
    }
`;